import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../../integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';
import { Badge } from '../../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../ui/tooltip';
import { TrendingUp, TrendingDown, Minus, HelpCircle, BarChart3 } from 'lucide-react';
import axios from "axios";
import { apiUrl, clientheaderconfig } from "../../../Helpers/Config";

interface Prize {
  id: string;
  voucherCustomName: string;
  probability: number;
  colorCode: string;
  is_active: boolean;
  voucherID: {
    label: string;
    value: string;
  };
}

interface SpinRow {
  prize_id: string | null;
  prize_text: string;
  spin_timestamp: string;
  voucherProductID: string
}

interface PrizeStat {
  prize_id: string;
  text: string;
  colorCode: string;
  voucherID: {
    label: string;
    value: string;
  };
  configured: number; // %
  observed: number; // %
  wins: number;
  expectedWins: number;
  delta: number; // observed - configured (percentage points)
  ci95Low: number; // 95% CI lower bound for observed (%)
  ci95High: number; // 95% CI upper bound for observed (%)
  withinCI: boolean; // is configured value within observed CI?
}

interface PrizeProps {
  prizeData:Prize[]
}

const TIME_WINDOWS = [
  { value: '24', label: 'Last 24 hours' },
  { value: '168', label: 'Last 7 days' },
  { value: '720', label: 'Last 30 days' },
  { value: '2160', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
];

// Wilson score interval for a binomial proportion (more accurate than normal approx for small n)
const wilsonInterval = (wins: number, n: number): [number, number] => {
  if (n === 0) return [0, 0];
  const z = 1.96;
  const p = wins / n;
  const denom = 1 + (z * z) / n;
  const center = (p + (z * z) / (2 * n)) / denom;
  const margin = (z * Math.sqrt((p * (1 - p)) / n + (z * z) / (4 * n * n))) / denom;
  return [Math.max(0, (center - margin) * 100), Math.min(100, (center + margin) * 100)];
};

const ProbabilityAnalytics: React.FC = ({prizeData}: PrizeProps) => {
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [spins, setSpins] = useState<SpinRow[]>([]);
  const [timeWindow, setTimeWindow] = useState('720');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      const since = timeWindow === 'all'
        ? null
        : new Date(Date.now() - parseInt(timeWindow) * 60 * 60 * 1000).toISOString();

      //const prizesQ = supabase.from('prizes').select('id, text, probability, color, is_active');

      const { data: results } = await axios.get(`${apiUrl}analytics?creditType=SpinWheel&spin_timestamp=${since}`, clientheaderconfig);
    const spinData = results?.result || [];


     /*  let spinsQ = supabase.from('spin_history').select('prize_id, prize_text, spin_timestamp');
      if (since) spinsQ = spinsQ.gte('spin_timestamp', since); */

      /* const [{ data: prizeData }, { data: spinData }] = await Promise.all([prizesQ, spinsQ]); */
      console.log(prizeData, spinData, 'prizeDataprizeDataprizeData')
      setPrizes((prizeData as Prize[]) || []);
      setSpins((spinData as SpinRow[]) || []);
      setLoading(false);
    };
    load();
  }, [timeWindow]);

  const stats: PrizeStat[] = useMemo(() => {
    const totalSpins = spins.length;
    if (totalSpins === 0 || prizes.length === 0) return [];

    // Group wins by prize_id (fall back to prize_text for legacy rows)
    const winsByPrizeId = new Map<string, number>();
    const winsByText = new Map<string, number>();
    spins.forEach((s) => {
      if (s.prize_id) winsByPrizeId.set(s.prize_id, (winsByPrizeId.get(s.prize_id) || 0) + 1);
      else winsByText.set(s.prize_text, (winsByText.get(s.prize_text) || 0) + 1);
    });

    return prizes
      .filter((p) => p.is_active)
      .map((p) => {
        const wins = (winsByPrizeId.get(p.voucherID.value) || 0) + (winsByText.get(p.voucherCustomName) || 0);
        const configured = Number(p.probability);
        const observed = (wins / totalSpins) * 100;
        const expectedWins = (configured / 100) * totalSpins;
        const [ciLow, ciHigh] = wilsonInterval(wins, totalSpins);
        const withinCI = configured >= ciLow && configured <= ciHigh;
        return {
          prize_id: p.id,
          text: p.voucherCustomName,
          colorCode: p.colorCode,
          voucherID: p.voucherID, 
          configured,
          observed,
          wins,
          expectedWins,
          delta: observed - configured,
          ci95Low: ciLow,
          ci95High: ciHigh,
          withinCI,
        };
      })
      .sort((a, b) => b.configured - a.configured);
  }, [prizes, spins]);

  const totalSpins = spins.length;
  const maxScale = Math.max(
    100,
    ...stats.map((s) => Math.max(s.configured, s.observed, s.ci95High))
  );

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Header / controls */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Configured vs Observed Win Rate
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Compare each prize's configured probability against how often it actually won.
            </p>
          </div>
          <Select value={timeWindow} onValueChange={setTimeWindow}>
            <SelectTrigger className="w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIME_WINDOWS.map((w) => (
                <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Summary card */}
        <Card>
          <CardContent className="p-4 flex flex-wrap gap-6">
            <div>
              <p className="text-xs text-muted-foreground">Total spins in window</p>
              <p className="text-2xl font-bold">{totalSpins}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Active prizes</p>
              <p className="text-2xl font-bold">{stats.length}</p>
            </div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                Statistical significance
                <Tooltip>
                  <TooltipTrigger><HelpCircle className="h-3 w-3" /></TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="text-xs">
                      Below ~100 spins, observed rates can differ wildly from configured ones by pure chance.
                      Trust the comparison more as the spin count grows.
                    </p>
                  </TooltipContent>
                </Tooltip>
              </p>
              <p className="text-sm">
                {totalSpins < 50 && <Badge variant="destructive">Very low — not reliable</Badge>}
                {totalSpins >= 50 && totalSpins < 200 && <Badge variant="secondary">Low — interpret carefully</Badge>}
                {totalSpins >= 200 && totalSpins < 1000 && <Badge>Moderate — useful trend</Badge>}
                {totalSpins >= 1000 && <Badge className="bg-green-600">Strong — reliable</Badge>}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Per-prize comparison */}
        <Card>
          <CardHeader>
            <CardTitle>Per-Prize Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-40 bg-muted animate-pulse rounded-lg" />
            ) : stats.length === 0 ? (
              <p className="text-sm text-muted-foreground">No spin data in this window yet.</p>
            ) : (
              <div className="space-y-5">
                {stats.map((s) => {
                  const Icon = Math.abs(s.delta) < 1 ? Minus : s.delta > 0 ? TrendingUp : TrendingDown;
                  const deltaColor =
                    s.withinCI ? 'text-muted-foreground'
                      : s.delta > 0 ? 'text-orange-600'
                      : 'text-blue-600';

                  return (
                    <div key={s.prize_id} className="space-y-2">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0">
                          <span
                            className="w-3 h-3 rounded-full shrink-0"
                            style={{ backgroundColor: s.colorCode }}
                          />
                          <span className="font-medium truncate">{s.text}</span>
                          {s.withinCI ? (
                            <Badge variant="secondary" className="text-xs">On target</Badge>
                          ) : (
                            <Badge variant="outline" className={`text-xs ${deltaColor}`}>
                              Off target
                            </Badge>
                          )}
                        </div>
                        <div className={`flex items-center gap-1 text-sm font-medium ${deltaColor}`}>
                          <Icon className="h-4 w-4" />
                          {s.delta > 0 ? '+' : ''}{s.delta.toFixed(1)} pts
                        </div>
                      </div>

                      {/* Dual bar: configured (light) vs observed (solid) */}
                      <div className="relative h-6 bg-muted rounded-md overflow-hidden">
                        {/* Configured bar */}
                        <div
                          className="absolute top-0 left-0 h-full opacity-30"
                          style={{
                            width: `${(s.configured / maxScale) * 100}%`,
                            backgroundColor: s.colorCode,
                          }}
                        />
                        {/* Observed bar */}
                        <div
                          className="absolute top-0 left-0 h-full"
                          style={{
                            width: `${(s.observed / maxScale) * 100}%`,
                            backgroundColor: s.colorCode,
                            borderRight: '2px solid rgba(0,0,0,0.4)',
                          }}
                        />
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                        <div>
                          <p className="text-muted-foreground">Configured</p>
                          <p className="font-semibold">{s.configured.toFixed(1)}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Observed</p>
                          <p className="font-semibold">{s.observed.toFixed(1)}%</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Wins / Expected</p>
                          <p className="font-semibold">{s.wins} / {s.expectedWins.toFixed(1)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground flex items-center gap-1">
                            95% CI
                            <Tooltip>
                              <TooltipTrigger><HelpCircle className="h-3 w-3" /></TooltipTrigger>
                              <TooltipContent className="max-w-xs">
                                <p className="text-xs">
                                  We're 95% confident the true win rate falls in this range.
                                  If the configured value sits inside it, the prize is behaving as expected.
                                </p>
                              </TooltipContent>
                            </Tooltip>
                          </p>
                          <p className="font-semibold">
                            {s.ci95Low.toFixed(1)}% – {s.ci95High.toFixed(1)}%
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Explainer */}
        <Card className="bg-muted/40">
          <CardContent className="p-4 text-xs text-muted-foreground space-y-1">
            <p><strong>How to read this:</strong></p>
            <p>• <strong>Configured</strong> = the probability you set in Prize Management.</p>
            <p>• <strong>Observed</strong> = how often that prize actually won during the selected window.</p>
            <p>• <strong>95% CI</strong> = statistical range the true rate likely sits in. Random variation shrinks as spin count grows.</p>
            <p>• A prize labeled "On target" is behaving exactly as configured — short streaks of wins or losses are normal.</p>
          </CardContent>
        </Card>
      </div>
    </TooltipProvider>
  );
};

export default ProbabilityAnalytics;
